import { NullableClassName } from '@squareup/dex-types-shared-ui';
import { Box } from '@squareup/dex-ui-shared-base';
import { setLocalAndForwardedRef } from '@squareup/dex-utils-dom';
import clsx from 'clsx';
import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { observe } from 'react-intersection-observer';

import styles from './navigation.module.css';

interface NavigationProps {
  children?: ReactNode;
  testId?: string | undefined;
}

interface NavigationListProps {
  children: ReactNode;
  isSubNavigation?: boolean;
  isHorizontal?: boolean;
  enableOverflowShimmer?: boolean;
  testId?: string | undefined;
}

const Navigation: FunctionComponent<NavigationProps & NullableClassName> = ({
  children,
  className,
  testId,
}) => {
  return (
    <nav data-testid={testId} className={clsx(styles.navigation, className)}>
      {children}
    </nav>
  );
};

const NavigationList = React.forwardRef<
  HTMLUListElement,
  NavigationListProps & NullableClassName
>(
  (
    {
      children,
      isSubNavigation,
      isHorizontal,
      enableOverflowShimmer,
      testId,
      className,
    },
    forwardedRef
  ) => {
    const [showShimmer, setShowShimmer] = useState(false);
    const listRef = useRef<HTMLUListElement>();

    useEffect(() => {
      const cleanupObservers: Array<() => void> = [];

      function handleScrollBottom() {
        if (!listRef.current) {
          return;
        }
        const anchors = listRef.current.getElementsByTagName('a');
        const lastAnchor = anchors?.[anchors?.length - 1];
        if (!lastAnchor) {
          return;
        }

        const cleanup = observe(
          lastAnchor,
          (inView) => {
            if (enableOverflowShimmer && !inView) {
              setShowShimmer(true);
            } else {
              setShowShimmer(false);
            }
          },
          { threshold: 1 }
        );

        cleanupObservers.push(cleanup);
      }

      if (!enableOverflowShimmer || !listRef.current) {
        setShowShimmer(false);
        return () => {};
      }

      handleScrollBottom();

      return () => {
        cleanupObservers.forEach((cleanup) => cleanup());
      };
    }, [enableOverflowShimmer]);

    return (
      <Box
        as={'ul'}
        data-testid={testId}
        ref={setLocalAndForwardedRef<HTMLUListElement>(listRef, forwardedRef)}
        className={clsx(
          styles.list,
          showShimmer && styles.shim,
          isSubNavigation && styles['sub-list'],
          isHorizontal && styles['horizontal-list'],
          className
        )}
        margin={{
          horizontal: isSubNavigation || isHorizontal ? undefined : '1x',
        }}
        padding={{
          bottom: isHorizontal ? undefined : '1x',
        }}
      >
        {children}
      </Box>
    );
  }
);

NavigationList.displayName = 'NavigationList';

export { type NavigationProps, Navigation, NavigationList };
